import React, { useEffect, useRef, useState } from 'react';
import BaseCarousel from '../base-carousel/BaseCarousel';
import {
  FlexContainer,
  RepeaterWrapper,
  Title,
  StyledLink,
  LinkWrapper,
} from './CarouselNewsEvents.style';
import Card from '../../cards/card/Card';
import { Responsive } from 'react-alice-carousel';
import { useMediaQuery, useTheme } from '@mui/material';
import { useWindowSize } from 'usehooks-ts';
import Repeater from '../../repeater/Repeater';
import PaginationArrows from '../../pagination-arrows/PaginationArrows';
import Link from 'next/link';
import { SliderConfigs } from '../../../models/domain/interfaces';
import {
  defaultSliderConfigs,
  fromHumanNumberOfColumnsToRepeaterNumberOfColumns,
} from '../../../utils/carousels';
import { NewsType } from '../../../models/auto-generated-strapi-types/types/NewsType';

interface Card {
  image?: any;
  category?: string;
  title?: string;
  description?: string;
  button?: any;
  url?: string;
  overlayColor?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  coverTextColor?: string;
  isLast?: boolean;
  types?: NewsType[];
}

interface NewsEventsCarouselProps {
  cards: Card[];
  itemPerPage?: Responsive;
  carouselTitle?: string;
  showCarouselTitle?: boolean;
  goToNewsEventsText: string;
  goToNewsEventsLink?: string;
  sliderConfigs?: SliderConfigs;
  isPrerenderRequest: boolean;
}

const NewsEventsCarousel: React.FC<NewsEventsCarouselProps> = ({
  sliderConfigs = defaultSliderConfigs,
  ...props
}: NewsEventsCarouselProps) => {
  const [columns, setColumns] = useState<number>(4);
  const [activeIndex, setActiveIndex] = useState(0);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('md'));
  const ref = useRef();
  const [date, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  const slidePrev = () => {
    (ref?.current as any)?.current?.slideTo?.(activeIndex - 1);
  };
  const slideNext = () => {
    (ref?.current as any)?.current?.slideTo?.(activeIndex + 1);
  };
  const syncActiveIndex = ({ item }: any) => setActiveIndex(item);

  const { width } = useWindowSize();
  const showCarouselTitle =
    props.showCarouselTitle !== false || sliderConfigs?.showCarouselTitle;

  useEffect(() => {
    setActiveIndex(0);
    switch (true) {
      case width > theme.breakpointsMediaQuery.values.lgl:
        setColumns(
          sliderConfigs.numberOfElementsXL ||
            (defaultSliderConfigs.numberOfElementsXL as number)
        );
        break;
      case width > theme.breakpointsMediaQuery.values.lg:
        setColumns(
          sliderConfigs.numberOfElementsLG ||
            (defaultSliderConfigs.numberOfElementsLG as number)
        );
        break;
      case width > theme.breakpointsMediaQuery.values.md:
        setColumns(
          sliderConfigs.numberOfElementsMD ||
            (defaultSliderConfigs.numberOfElementsMD as number)
        );
        break;
      case width > theme.breakpointsMediaQuery.values.sm:
        setColumns(
          sliderConfigs.numberOfElementsSM ||
            (defaultSliderConfigs.numberOfElementsSM as number)
        );
        break;
      case width > theme.breakpointsMediaQuery.values.xs:
        setColumns(
          sliderConfigs.numberOfElementsXS ||
            (defaultSliderConfigs.numberOfElementsXS as number)
        );
        break;

      default:
        setColumns(
          sliderConfigs.numberOfElementsXS ||
            (defaultSliderConfigs.numberOfElementsXS as number)
        );
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const cardsMapper = (cards: Card[]) =>
    cards?.map((card) => ({
      image: card?.image,
      category: card.category,
      title: card.title,
      description: card.description,
      types: card?.types,
      url: card.url,
      button: {
        ...card.button,
        link: card?.button?.url,
      },
      overlayColor: card.overlayColor,
      coverTextColor: card.coverTextColor,
      date: card.date,
      startDate: card.startDate,
      endDate: card.endDate,
      isLast: card.isLast,
      forceHideDate: sliderConfigs?.forceHideDate,
    }));

  const repeaterRender = (items: any[]) => {
    return items.reduce((acc, curr, i) => {
      if (!acc || !acc[Math.floor(i / columns)]) {
        acc = {
          ...acc,
          [Math.floor(i / columns)]: [curr],
        };
      } else {
        acc[`${Math.floor(i / columns)}`].push(curr);
      }
      return acc;
    }, {} as any);
  };

  const mappedCards = cardsMapper(props.cards);

  const carouselItems = Object.values(
    (props.cards &&
      Array.isArray(props.cards) &&
      repeaterRender(mappedCards)) ||
      []
  )?.map((items, i) => (
    <Repeater
      key={i}
      spacing={2}
      cols={{
        xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
          sliderConfigs.numberOfElementsXS ||
            (defaultSliderConfigs.numberOfElementsXS as number)
        ),
        sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
          sliderConfigs.numberOfElementsSM ||
            (defaultSliderConfigs.numberOfElementsSM as number)
        ),
        md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
          sliderConfigs.numberOfElementsMD ||
            (defaultSliderConfigs.numberOfElementsMD as number)
        ),
        lg: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
          sliderConfigs.numberOfElementsLG ||
            (defaultSliderConfigs.numberOfElementsLG as number)
        ),
        xl: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
          sliderConfigs.numberOfElementsXL ||
            (defaultSliderConfigs.numberOfElementsXL as number)
        ),
      }}
      data={items}
      props={{
        section: 'carousel-news-events',
        isPrerenderRequest: props.isPrerenderRequest,
      }}
      component={Card}
    />
  ));

  const data = mappedCards?.filter((item) => !item.isLast)?.slice(0, 3);

  return (
    <>
      {(typeof window === 'undefined' || isDesktop) && (
        <>
          <FlexContainer showCarouselTitle={showCarouselTitle}>
            {showCarouselTitle && <Title>{props.carouselTitle}</Title>}
            {carouselItems.length > 1 && (
              <PaginationArrows
                label={true}
                index={activeIndex}
                totalItems={carouselItems.length}
                onLeftArrowClick={slidePrev}
                onRightArrowClick={slideNext}
              />
            )}
          </FlexContainer>
          <BaseCarousel
            {...props}
            responsive={props.itemPerPage}
            mouseTracking={true}
            disableButtonsControls
            disableDotsControls
            activeIndex={activeIndex}
            onSlideChanged={syncActiveIndex}
            items={carouselItems}
            dotsAlignment={'right'}
            autoPlay={sliderConfigs.autoPlay}
            autoPlayInterval={sliderConfigs.autoPlayInterval}
            animationDuration={sliderConfigs.animationDuration}
            animationType={sliderConfigs.animationType}
            infinite={sliderConfigs.infinite}
            refFromParentCb={(refFromChild: any) => {
              ref.current = refFromChild;
            }}
          />
        </>
      )}
      {typeof window !== 'undefined' && !isDesktop && (
        <>
          <RepeaterWrapper>
            {showCarouselTitle && <Title>{props.carouselTitle}</Title>}
            {date !== 0 && (
              <>
                <Repeater
                  spacing={2}
                  cols={{
                    xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                      sliderConfigs.numberOfElementsXS ||
                        (defaultSliderConfigs.numberOfElementsXS as number)
                    ),
                    sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                      sliderConfigs.numberOfElementsSM ||
                        (defaultSliderConfigs.numberOfElementsSM as number)
                    ),
                    md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                      sliderConfigs.numberOfElementsMD ||
                        (defaultSliderConfigs.numberOfElementsMD as number)
                    ),
                    lg: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                      sliderConfigs.numberOfElementsLG ||
                        (defaultSliderConfigs.numberOfElementsLG as number)
                    ),
                    xl: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                      sliderConfigs.numberOfElementsXL ||
                        (defaultSliderConfigs.numberOfElementsXL as number)
                    ),
                  }}
                  data={data}
                  props={{
                    section: 'carousel-news-events',
                    isPrerenderRequest: props.isPrerenderRequest,
                  }}
                  component={Card}
                />
                {props.goToNewsEventsLink && (
                  <LinkWrapper>
                    <Link href={`/${props.goToNewsEventsLink}`} passHref>
                      <StyledLink>{props.goToNewsEventsText}</StyledLink>
                    </Link>
                  </LinkWrapper>
                )}
              </>
            )}
          </RepeaterWrapper>
        </>
      )}
    </>
  );
};

export default NewsEventsCarousel;
